import type { FingerFlags } from './fingerCount'

// Hands-free commands are poses held still for a moment while a ring fills, so
// they never fire mid-phrase: two fingers up cycles the voice, a thumbs-up
// toggles the echo garden. Playing poses (open hand, fist, pinch) are untouched.

export type HandCommand = 'next-voice' | 'toggle-echo'

export interface HandCommandState {
  command: HandCommand | null
  progress: number
  fired: HandCommand | null
}

export interface HandCommandOptions {
  dwellMs?: number
  cooldownMs?: number
}

export const COMMAND_LABELS: Record<HandCommand, string> = { 'next-voice': 'NEXT VOICE', 'toggle-echo': 'ECHO' }

export function poseCommand(flags: FingerFlags): HandCommand | null {
  if (flags.index && flags.middle && !flags.ring && !flags.pinky) return 'next-voice'
  if (flags.thumb && !flags.index && !flags.middle && !flags.ring && !flags.pinky) return 'toggle-echo'
  return null
}

export class HandCommandDetector {
  private readonly dwellMs: number
  private readonly cooldownMs: number
  private current: HandCommand | null = null
  private sinceMs = 0
  private firedAtMs = Number.NEGATIVE_INFINITY
  private firedFor: HandCommand | null = null

  constructor(options: HandCommandOptions = {}) {
    this.dwellMs = options.dwellMs ?? 650
    this.cooldownMs = options.cooldownMs ?? 900
  }

  push(flags: FingerFlags | null, timestampMs: number): HandCommandState {
    const command = flags ? poseCommand(flags) : null
    if (command !== this.current) {
      this.current = command
      this.sinceMs = timestampMs
      this.firedFor = null
    }
    if (!command) return { command: null, progress: 0, fired: null }
    if (this.firedFor === command) return { command, progress: 1, fired: null }
    if (timestampMs - this.firedAtMs < this.cooldownMs) return { command, progress: 0, fired: null }
    const progress = Math.min(1, (timestampMs - this.sinceMs) / this.dwellMs)
    if (progress >= 1) {
      this.firedFor = command
      this.firedAtMs = timestampMs
      return { command, progress: 1, fired: command }
    }
    return { command, progress, fired: null }
  }

  reset(): void {
    this.current = null
    this.sinceMs = 0
    this.firedFor = null
  }
}
